import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'

const SESSION_EXPIRY_MS = FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
const MAX_CACHE_SIZE = 50

export interface FareFamilySelection {
  departingFareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
}

interface FareFamilySelectionCacheEntry extends FareFamilySelection {
  searchId: string
  cachedAt: number
}

interface FareFamilySelectionCacheKeyArgs {
  departingJourneyKey: string | undefined
  returningJourneyKey?: string
  searchId: string | undefined
}

const cache = new Map<string, FareFamilySelectionCacheEntry>()

function getCacheKey(departingJourneyKey: string, returningJourneyKey: string | undefined): string {
  return `${departingJourneyKey}|${returningJourneyKey ?? ''}`
}

function evictExpired() {
  const now = Date.now()
  for (const [key, entry] of cache) {
    if (now - entry.cachedAt > SESSION_EXPIRY_MS) {
      cache.delete(key)
    }
  }
}

/**
 * Remembers the fare the traveller picked for a journey (or return pair) so the
 * cart conversion that happens later — once the assistant has created the trip
 * item — can reuse it instead of re-opening the fare modal.
 *
 * Fare family ids only mean something within the provider search that produced
 * them, so the entry is stamped with its `searchId` and only returned to a
 * lookup quoting the same one.
 */
export function cacheFareFamilySelection(
  { departingJourneyKey, returningJourneyKey, searchId }: FareFamilySelectionCacheKeyArgs,
  selection: FareFamilySelection,
) {
  if (!departingJourneyKey || !searchId) return
  if (!selection.departingFareFamily && !selection.returningFareFamily) return

  evictExpired()

  const key = getCacheKey(departingJourneyKey, returningJourneyKey)
  if (cache.size >= MAX_CACHE_SIZE && !cache.has(key)) {
    const oldestKey = [...cache.entries()].sort(([, a], [, b]) => a.cachedAt - b.cachedAt)[0]?.[0]
    if (oldestKey) cache.delete(oldestKey)
  }

  cache.set(key, {
    departingFareFamily: selection.departingFareFamily,
    returningFareFamily: selection.returningFareFamily,
    searchId,
    cachedAt: Date.now(),
  })
}

export function getCachedFareFamilySelection(
  { departingJourneyKey, returningJourneyKey, searchId }: FareFamilySelectionCacheKeyArgs,
): FareFamilySelection | undefined {
  if (!departingJourneyKey || !searchId) return undefined

  const key = getCacheKey(departingJourneyKey, returningJourneyKey)
  const entry = cache.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.cachedAt > SESSION_EXPIRY_MS) {
    cache.delete(key)
    return undefined
  }
  if (entry.searchId !== searchId) return undefined

  return {
    departingFareFamily: entry.departingFareFamily,
    returningFareFamily: entry.returningFareFamily,
  }
}

/** Test seam — the cache is module state shared by every consumer. */
export function clearFareFamilySelectionCache() {
  cache.clear()
}
